import type { ComponentProps, ComponentPropsWithoutRef, ElementType } from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';

import Tooltip from '@/components/Tooltip';
import Typography from '@/components/Typography';

import type { ExtraProps } from 'react-markdown';

export const MarkdownLink: ElementType<ComponentProps<'a'> & ExtraProps> = ({
  node,
  children,
  ...props
}) => (
  <Tooltip
    title='Click to see index information'
    variant='secondary'
  >
    <Link
      {...props}
      color='primary'
      rel='noopener noreferrer'
      sx={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
      target='_blank'
    >
      {children}
      <ChevronRightIcon sx={{ fontSize: '1em' }} />
    </Link>
  </Tooltip>
);

export const MarkdownH1: ElementType<ComponentProps<'h1'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={{ fontSize: '20px', mt: 3, mb: 1.5, fontWeight: 700 }}
    variant='h1'
    {...props}
  />
);

export const MarkdownH2: ElementType<ComponentProps<'h2'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={{ fontSize: '16px', mt: 3, mb: 1.5, fontWeight: 700 }}
    variant='h2'
    {...props}
  />
);

export const MarkdownH3: ElementType<ComponentProps<'h3'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={{ fontSize: '14px', mt: 3, mb: 1.5, fontWeight: 700 }}
    variant='h3'
    {...props}
  />
);

export const MarkdownH4: ElementType<ComponentProps<'h4'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={{ fontSize: '14px', mt: 3, mb: 1.5, fontWeight: 700 }}
    variant='h4'
    {...props}
  />
);

export const MarkdownH5: ElementType<ComponentProps<'h5'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={{ fontSize: '13px', mt: 3, mb: 1.5, fontWeight: 700 }}
    variant='h5'
    {...props}
  />
);

export const MarkdownH6: ElementType<ComponentProps<'h6'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={{ fontSize: '12px', mt: 3, mb: 1.5, fontWeight: 700 }}
    variant='h6'
    {...props}
  />
);

export const MarkdownBlockquote: ElementType<ComponentProps<'blockquote'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <Box
    {...props}
    component='blockquote'
    sx={{
      borderLeft: '4px solid',
      borderColor: 'divider',
      pl: 2,
      my: 1.5,
      mx: 0,
      color: 'text.secondary'
    }}
  />
);

export const MarkdownCode: ElementType<ComponentProps<'code'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <Box
    {...props}
    component='code'
    sx={{
      backgroundColor: 'action.hover',
      borderRadius: '4px',
      px: 0.5,
      py: 0.25,
      fontSize: '13px',
      fontFamily: 'monospace'
    }}
  />
);

export const MarkdownPre: ElementType<ComponentProps<'pre'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <Box
    {...props}
    component='pre'
    sx={{
      backgroundColor: 'action.hover',
      borderRadius: '4px',
      p: 2,
      my: 1.5,
      overflow: 'auto',
      fontSize: '13px',
      fontFamily: 'monospace'
    }}
  />
);

export const MarkdownHr: ElementType<ComponentProps<'hr'> & ExtraProps> = ({ node, ...props }) => (
  <Divider
    {...props}
    sx={{ my: 3 }}
  />
);

export const MarkdownDel: ElementType<ComponentProps<'del'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <Box
    {...props}
    component='del'
    sx={{ textDecoration: 'line-through' }}
  />
);

export const MarkdownTable: ElementType<ComponentProps<'table'> & ExtraProps> = ({
  node,
  ...props
}) => <Table {...props} />;

export const MarkdownThead: ElementType<ComponentProps<'thead'> & ExtraProps> = ({
  node,
  ...props
}) => <TableHead {...props} />;

export const MarkdownTbody: ElementType<ComponentProps<'tbody'> & ExtraProps> = ({
  node,
  ...props
}) => <TableBody {...props} />;

export const MarkdownTr: ElementType<ComponentProps<'tr'> & ExtraProps> = ({ node, ...props }) => (
  <TableRow {...props} />
);

export const MarkdownImg: ElementType<ComponentProps<'img'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <Box
    {...props}
    component='img'
    sx={{ maxWidth: '100%', height: 'auto', display: 'block', my: 1.5 }}
  />
);

export const MarkdownUl: ElementType<ComponentProps<'ul'> & ExtraProps> = ({ node, ...props }) => (
  <Box
    {...props}
    component='ul'
    sx={{ pl: 3, my: 1.5 }}
  />
);

export const MarkdownOl: ElementType<ComponentProps<'ol'> & ExtraProps> = ({ node, ...props }) => (
  <Box
    {...props}
    component='ol'
    sx={{ pl: 3, my: 1.5 }}
  />
);

export const MarkdownLi: ElementType<ComponentProps<'li'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    {...props}
    component='li'
    sx={{ fontSize: '14px', mb: 0.5 }}
  />
);

export const MarkdownP: ElementType<ComponentProps<'p'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    {...props}
    component='p'
    sx={{ fontSize: '14px', my: 1.5, lineHeight: 1.7 }}
  />
);

export const MarkdownStrong: ElementType<ComponentProps<'strong'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <Box
    {...props}
    component='strong'
    sx={{ fontWeight: 700 }}
  />
);

export const MarkdownEm: ElementType<ComponentProps<'em'> & ExtraProps> = ({ node, ...props }) => (
  <Box
    {...props}
    component='em'
    sx={{ fontStyle: 'italic' }}
  />
);

const spanClassToColor: Record<string, string> = {
  muted: 'text.disabled',
  error: 'error.main',
  primary: 'primary.main',
  secondary: 'text.secondary'
};

export const MarkdownSpan: ElementType<ComponentProps<'span'> & ExtraProps> = ({
  node,
  className,
  ...props
}) => {
  const color = className ? spanClassToColor[className] : undefined;
  return (
    <Box
      {...props}
      component='span'
      sx={{ ...(color && { color }) }}
    />
  );
};

export const MarkdownTableCell: ElementType<
  (ComponentProps<'th'> | ComponentProps<'td'>) & ExtraProps
> = ({ node, ...props }) => {
  const isHeader = node?.tagName === 'th';
  const mappedProps: ComponentPropsWithoutRef<typeof TableCell> = {
    ...props,
    align: props.align === 'char' ? 'inherit' : props.align
  };
  return (
    <TableCell
      align={isHeader ? 'center' : 'left'}
      sx={{
        border: '0.5px solid white',
        px: 1.5,
        py: 1,
        fontSize: '14px',
        ...(isHeader
          ? {
              fontWeight: 700
            }
          : {})
      }}
      {...mappedProps}
    />
  );
};
